import { useState } from "react";

import useService from "../../../../../hooks/useService";
import { getModalHandler } from "../../../Common/Modals/hooks/useModalSingleton";
import STRINGS from "../utils/strings";

export default function useProviderHoursEditor({ providerId, refreshTimings }) {
  const officeHoursRepo = useService("repositories/provider-office-hours-repository");

  const { openSuccessModal, openFailureModal } = getModalHandler();
  const [editingDays, setEditingDays] = useState(() =>
    Array.from({ length: STRINGS.DAYS_IN_WEEK }, () => false)
  );

  const [draftHoursByDay, setDraftHoursByDay] = useState({});
  const [hoursToUpdate, setHoursToUpdate] = useState(new Map());
  const [hoursToDelete, setHoursToDelete] = useState(new Set());

  const startEditingDay = (dayIndex, existingHours) => {
    setEditingDays((prev) => prev.map((_, index) => index === dayIndex));

    setDraftHoursByDay((prev) => ({
      ...prev,
      [dayIndex]: existingHours.length
        ? existingHours.map((hour) => ({ ...hour }))
        : [{ startTime: "", endTime: "" }],
    }));
  };

  const stopEditingDay = (dayIndex) => {
    setEditingDays(() => Array(STRINGS.DAYS_IN_WEEK).fill(false));
    setDraftHoursByDay((prev) => {
      const copy = { ...prev };
      delete copy[dayIndex];
      return copy;
    });
  };

  const updateHourField = (dayIndex, hourIndex, field, value) => {
    setDraftHoursByDay((prev) => {
      const updatedHours = prev[dayIndex].map((hour, index) =>
        index === hourIndex ? { ...hour, [field]: value } : hour
      );

      const updatedHour = updatedHours[hourIndex];

      if (updatedHour.id) {
        setHoursToUpdate((prevUpdates) => {
          const updates = new Map(prevUpdates);
          updates.set(updatedHour.id, {
            id: updatedHour.id,
            startTime: updatedHour.startTime,
            endTime: updatedHour.endTime,
          });
          return updates;
        });
      }

      return { ...prev, [dayIndex]: updatedHours };
    });
  };

  const addHourWindow = (dayIndex) => {
    setDraftHoursByDay((prev) => ({
      ...prev,
      [dayIndex]: [...prev[dayIndex], { startTime: "", endTime: "" }],
    }));
  };

  const removeHourWindow = (dayIndex, hourIndex) => {
    const hour = draftHoursByDay[dayIndex][hourIndex];
    if (hour.id) {
      setHoursToDelete((prev) => new Set(prev).add(hour.id));

      setHoursToUpdate((prev) => {
        const updates = new Map(prev);
        updates.delete(hour.id);
        return updates;
      });
    }

    setDraftHoursByDay((prev) => ({
      ...prev,
      [dayIndex]: prev[dayIndex].filter((_, index) => index !== hourIndex),
    }));
  };

  const saveDayHours = async (dayIndex) => {
    try {
      const dayHours = draftHoursByDay[dayIndex] || [];

      const createPayload = dayHours
        .filter((hour) => !hour.id && hour.startTime && hour.endTime)
        .map((hour) => ({
          provider: providerId,
          day: dayIndex,
          start_time: hour.startTime,
          end_time: hour.endTime,
        }));

      const updatePayload = [...hoursToUpdate.values()];
      const deletePayload = [...hoursToDelete];

      const requests = [];

      if (createPayload.length) {
        requests.push(officeHoursRepo.bulkCreate(createPayload));
      }

      if (updatePayload.length) {
        requests.push(officeHoursRepo.bulkUpdate(updatePayload));
      }

      if (deletePayload.length) {
        requests.push(officeHoursRepo.bulkDelete(deletePayload));
      }

      if (requests.length > 0) {
        await Promise.all(requests).then(async () => {
          openSuccessModal();
          await refreshTimings();
          stopEditingDay(dayIndex);
          setHoursToUpdate(new Map());
          setHoursToDelete(new Set());
        });
      }
    } catch (error) {
      console.error(error);
      openFailureModal(error);
    }
  };

  return {
    editing: editingDays,
    draftTimings: draftHoursByDay,
    startEditing: startEditingDay,
    stopEditing: stopEditingDay,
    updateTiming: updateHourField,
    addWindow: addHourWindow,
    deleteWindow: removeHourWindow,
    saveTimings: saveDayHours,
  };
}
